import {MobileNativeAudio, NewMobileNativeApi} from "../MobileNativeApi";
import {Observable, Observer, of, Subject} from "rxjs";
import {LoggerService} from "../../../services/logger.service";
import {ExerciseSession, ItemAvailability} from "../../../model/personal";
import {Chapter, ExerciseSet} from "../../../model/struct";
import {Question} from "../../../model/questions";
import {environment} from "../../../../environments/environment";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {switchMap} from "rxjs/operators";
import {Injectable} from "@angular/core";

/**
 * Methods which may be called by native in web app
 */
interface AndroidWebBridge {
  /**
   * call after you provide native bridge implementation to the WebView
   */
  initialize();
  /**
   * Call if data state has been changed and you
   * expect the current View data should be reloaded
   */
  notifyDataUpdate();
}

/**
 * Methods which may be called by web in native
 */
interface NativeBridge {

  /**
   * store the data for the coming request. Request is identified by provided numeric value
   * which will be provided in "X-Request-Id" header
   * @param id of the request which data belongs to
   */
  storeRequestData(id: string, data: string): void;

  /**
   * clear the recording buffer
   * @param id request id
   */
  clear(id: number): void;

  /**
   * start sound recording
   * @param id request id
   */
  record(id: number): void;

  /**
   * stop sound recording or playing
   * @param id
   */
  stop(id: number): void;

  /**
   * play recorded sound (if is still recording stop recording first)
   * @param id
   */
  play(id: number): void;

  /**
   * terminate (release) the recording system (for example you may need to stop recording,
   * release a media player or media recorder
   * @param id
   */
  terminate(id: number): void;

  /**
   * Initialize sound system. I.e. ask for privileges
   * @param id
   * @return true if is initialized correctly and allows to record the sound
   */
  init(id: number): boolean;

  /**
   * close the web application. Remember check if sound recording device is terminated
   */
  close(id: number): void;
}

/**
 * Temporary structure to keep requests invocation until AndroidWebBridge.initialize() method
 * call by native
 */
class WaitingCall<T> {
  constructor(
    public id: number,
    private nativeCall: (id: number) => T,
    private observer: Observer<T>
  ) {}
  public doCall() {
    try {
      const result = this.nativeCall(this.id);
      this.observer.next(result);
      this.observer.complete;
    } catch (e) {
      this.observer.error(e);
    }
  }
}

/**
 * The service is responsible for the communication between native and web part.
 * Before a full communication channel would be established the method initialize() has to be invoked first
 * by the native.
 *
 * The communication uses two different techniques. Methods related to the sound recording device
 * calls native api directly. Other uses a REST protocol which has to be intercepted by a native.
 * For data manipulation requests like POST or PUT we had to prepare a special
 * protocol for data providing (because request body can't be read during the native intercept phase).
 * For that cases api calls storeRequestData() method which provides a request body
 * before a regular request call.
 *
 * The URL scheme is formatted in typical tree structure:
 * + /sets +                                              - set definitions container
 * |       + /${exerciseSet} +                            - specific set content definitions container (chapters)
 * |                         + availability               - specific set availabilities container
 * |                         + questions                  - specific set question definitions container
 * + sessions +                                           - device stored sessions container
 *            + current                                   - device current session content
 */
@Injectable({
  providedIn: 'root'
})
export class AndroidRequestBasedNativeApi implements NewMobileNativeApi, MobileNativeAudio, AndroidWebBridge {

  initialized = false;
  updateEventsSubject = new Subject<void>();
  waitingCallsQueue: WaitingCall<any>[] = [];
  private refreshingSubject = new Subject<Observable<void>>();
  id = 0;
  // endpoint for fake http intercepted calls
  private apiEndpoint = environment.apiEndpoint;

  constructor(
    private logger: LoggerService,
    private http: HttpClient
    ) {
    logger.log('creating android native bridge');
    window.webBridge = this;
  }

  /**
   * build full request URL value basing on the relative path
   *
   * with the current environment value the call would be the same as { return `http://iternal-api-calls/api/${rest of the path}; }
   *
   * @param path
   * @private
   */
  private buildPath(path: string) {
    return `${this.apiEndpoint}/api${path}`;
  }

  initialize() {
    this.logger.log(`initializing with ${this.waitingCallsQueue.length} waiting tasks.`);
    this.initialized = true;
    for (const waitingCall of this.waitingCallsQueue) {
      this.logger.log(`calling native during init [${waitingCall.id}]`);
      waitingCall.doCall();
    }
    this.waitingCallsQueue = [];
  }

  /**
   * internal implementation of native calls which will take care for
   * waiting calls stored before initialization
   * @param nativeCall
   * @private
   */
  private callNative<T>(nativeCall: (id: number) => T): Observable<T> {
    const id = this.getNextRequestId();
    if (this.initialized) {
      this.logger.log(`calling native [${id}] directly`);
      return of(nativeCall(id));
    }
    this.logger.log(`adding to waiting tasks [${id}]`)
    return new Observable<T>(observer => {
      this.waitingCallsQueue.push(new WaitingCall(id, nativeCall, observer));
    });
  }

  /**
   * generic method to store the POST, PUT request data before
   * request invocation
   * @param data
   */
  private storeRequestData(data: any): Observable<number> {
    return this.callNative(id => {
      (window.nativeBridge as NativeBridge).storeRequestData(id.toString(), JSON.stringify(data));
      return id;
    })
  }

  notifyDataUpdate(): void {
    this.logger.log('notifying data update');
    this.updateEventsSubject.next();
  }

  listenForDataUpdates(): Observable<void> {
    return this.updateEventsSubject;
  }

  log(text: string) {
    this.logger.log(`[android-api2] - ${text}`);
  }

  clear(): Observable<void> {
    this.log("calling clear");
    return this.callNative<void>(
      id => (window.nativeBridge as NativeBridge).clear(id)
    );
  }

  /**
   * start recording
   */
  record() {
    this.log("calling record");
    return this.callNative<void>(
      id => (window.nativeBridge as NativeBridge).record(id)
    ).subscribe();
  }

  /**
   * stop recording or playback
   */
  stop(): Observable<void> {
    this.log("calling stop");
    return this.callNative<void>(id => (window.nativeBridge as NativeBridge).stop(id));
  }

  /**
   * start recording playback
   */
  play2() {
    this.log("calling play");
    return this.callNative<void>(
      id => (window.nativeBridge as NativeBridge).play(id)
    ).subscribe();
  }

  /**
   * clean up and release recording device
   */
  terminate() {
    this.log("calling terminate");
    return this.callNative<void>(
      id => (window.nativeBridge as NativeBridge).terminate(id)
    ).subscribe();
  }

  /**
   * initialize recording device, returns true if positive
   * @param callback
   */
  initAudio(callback: (state: boolean) => void) {
    this.log(" calling clear");
    return this.callNative<boolean>(
      id => (window.nativeBridge as NativeBridge).init(id)
    ).subscribe(
      it => callback(it)
    )
  }

  // #### data management implementation #####
  private getNextRequestId() {
    return this.id++;
  }

  private static buildRequestIdHeader(id: number){
    return new HttpHeaders({"X-Request-Id" : id.toString()});
  }

  private nextRequestIdHeader(){
    return AndroidRequestBasedNativeApi.buildRequestIdHeader(this.getNextRequestId());
  }

  /**
   * read the availability JSON file for exerciseSet [GET] /sets/${exerciseSet}/availability
   * @param exerciseSet
   */
  getAvailabilities(exerciseSet: string): Observable<ItemAvailability[]> {
    this.log(`getting availabilities for ${exerciseSet}`);
    return this.http.get<ItemAvailability[]>(this.buildPath(`/sets/${exerciseSet}/availability`), {headers: this.nextRequestIdHeader()})
  }

  /**
   * read chapters JSON file for exerciseSet [GET] /sets/${exerciseSet}
   * @param exerciseSet
   */
  getChapters(exerciseSet: string): Observable<Chapter[]> {
    this.log(`getting chapters for ${exerciseSet}`);
    return this.http.get<Chapter[]>(this.buildPath(`/sets/${exerciseSet}`), {headers: this.nextRequestIdHeader()});
  }

  /**
   * read current session JSON file [GET] /sessions/current
   */
  getCurrentSession(): Observable<ExerciseSession> {
    this.log(`getting current session`);
    return this.http.get<ExerciseSession>(this.buildPath(`/sessions/current`), {headers: this.nextRequestIdHeader()});
  }

  /**
   * read questions file for exerciseSet [GET] /sets/${exerciseSet}/questions
   * @param exerciseSet
   */
  getQuestions(exerciseSet: string): Observable<Question<any, any>[]> {
    this.log(`getting questions for ${exerciseSet}`);
    return this.http.get<Question<any,any>[]>(this.buildPath(`/sets/${exerciseSet}/questions`), {headers: this.nextRequestIdHeader()});
  }

  /**
   * read exercise sets file (has to be synced by the native app !!!!) [GET] /sets
   */
  listExerciseSets(): Observable<ExerciseSet[]> {
    this.log('getting exercise sets');
    return this.http.get<ExerciseSet[]>(this.buildPath(`/sets`), {headers: this.nextRequestIdHeader()});
  }

  /**
   * push session to the FIFO sync queue [POST] /sessions {request body - session to store, response body - stored session}
   * @param session
   */
  pushSession(session: ExerciseSession): Observable<ExerciseSession> {
    this.log(`pushing session ${session.deviceUUID}`);
    return this.storeRequestData(session).pipe(
      switchMap( id => this.http.post<ExerciseSession>(this.buildPath("/sessions"), {}, {headers: AndroidRequestBasedNativeApi.buildRequestIdHeader(id)}))
    )
  }

  /**
   * send stored sync sessions queue to the API and clean up the queue if positive. [POST] /sessions/sync {request and response body is empty}
   * Sync in FIFO order
   */
  sendStoredSessions(): Observable<any> {
    this.log(`sending sessions to the server`);
    return this.http.post<void>(this.buildPath("/sessions/sync"), {headers: this.nextRequestIdHeader()})
  }

  /**
   * store availability in the file, dont change update time [PUT] /sets/${exerciseSet}/availability {request and response body has a data to write}
   * @param exerciseSet
   * @param availability
   */
  storeAvailability(exerciseSet: string, availability: ItemAvailability[]): Observable<ItemAvailability[]> {
    this.log(`storing availabilities for ${exerciseSet}`);
    return this.storeRequestData(availability).pipe(
      switchMap( id => this.http.put<ItemAvailability[]>(this.buildPath(`/sets/${exerciseSet}/availability`), {}, {headers: AndroidRequestBasedNativeApi.buildRequestIdHeader(id)}))
    )
  }

  /**
   * store session in current session file [PUT] /sessions/current {bodies are session to save}
   * @param session
   */
  storeCurrentSession(session: ExerciseSession): Observable<ExerciseSession> {
    this.log(`storing current session ${session.deviceUUID}`);
    return this.storeRequestData(session).pipe(
      switchMap( id => this.http.put<ExerciseSession>(this.buildPath("/sessions/current"), {}, {headers: AndroidRequestBasedNativeApi.buildRequestIdHeader(id)}))
    )
  }

  /**
   * Append or create http parameter to pass the sync frequency value in http request
   * @param freq frequency in Ms
   * @param paramsToAppend existing request parameters or empty if non exists
   */
  private static prepareFreqParams(freq: number, paramsToAppend?: HttpParams) {
    if (!paramsToAppend) {
      paramsToAppend = new HttpParams();
    }
    if (!freq) return paramsToAppend;
    return paramsToAppend.append("freq", freq.toString());
  }

  /**
   * do the file sync if required. Check if the existing version is older than @syncFrequencyMs [POST] /sets/${exerciseSet}/availability/sync {bodies are empty}
   * @param exerciseSet
   * @param syncFrequencyMs
   */
  syncAvailabilities(exerciseSet: string, syncFrequencyMs: number): Observable<any> {
    this.log(`syncing availabilities for ${exerciseSet}`);
    return this.http.post<any>(this.buildPath(`/sets/${exerciseSet}/availability/sync`), {}, {
      headers: this.nextRequestIdHeader(),
      params: AndroidRequestBasedNativeApi.prepareFreqParams(syncFrequencyMs)
    });
  }

  /**
   * do the file sync if required. Check if the existing version is older than @syncFrequencyMs [POST] /sets/${exerciseSet} {request body empty, response has chapters}
   * @param exerciseSet
   * @param syncFrequencyMs
   */
  syncChapters(exerciseSet: string, syncFrequencyMs: number): Observable<Chapter[]> {
    this.log(`syncing chapters for ${exerciseSet}`);
    return this.http.post<Chapter[]>(this.buildPath(`/sets/${exerciseSet}/sync`), {}, {
      headers: this.nextRequestIdHeader(),
      params: AndroidRequestBasedNativeApi.prepareFreqParams(syncFrequencyMs)
    });
  }

  /**
   * do the file sync if required. Check if the existing version is older than @syncFrequencyMs [POST] /sets/${exerciseSet}/questions/sync {bodies are empty}
   * @param exerciseSet
   * @param syncFrequencyMs
   */
  syncQuestions(exerciseSet: string, syncFrequencyMs: number): Observable<any> {
    this.log(`syncing questions for ${exerciseSet}`);
    return this.http.post(this.buildPath(`/sets/${exerciseSet}/questions/sync`), {}, {
      headers: this.nextRequestIdHeader(),
      params: AndroidRequestBasedNativeApi.prepareFreqParams(syncFrequencyMs)
    })
  }

  /**
   * do application close
   */
  close(): void {
    this.log('calling for close the app.');
    this.callNative<void>(id => (window.nativeBridge as NativeBridge).close(id)).subscribe();
  }

  // #### data management api #####
  subscribeForRefreshingEvents(): Observable<Observable<void>> {
    return this.refreshingSubject;
  }
}
